import { ApiPointsAuditLog } from '@api/interfaces';
import { FarmerReprocessingScope, PointsAuditEventType } from '@shared/enums';
import { TFunction } from 'i18next';

/** Turns one audit row into a plain-English sentence for CS. The raw diff is shown below it. */
export const describeAuditEvent = (
  row: ApiPointsAuditLog,
  t: TFunction,
): string => {
  const before = row.before ?? {};
  const after = row.after ?? {};
  const metadata = row.metadata ?? {};

  const number = (value: unknown) => (
    typeof value === 'number' ? value : Number(value ?? 0)
  );

  switch (row.eventType) {
    case PointsAuditEventType.ExclusionChanged:
      return after.excludeFromLoyaltyProgram
        ? t($ => $.pointsAudit.plain.excluded)
        : t($ => $.pointsAudit.plain.included);

    case PointsAuditEventType.MembershipChanged: {
      const isEnergyChange = before.energy !== after.energy;
      const program = isEnergyChange
        ? t($ => $.pointsAudit.programs.energy)
        : t($ => $.pointsAudit.programs.agronomy);
      const isJoining = isEnergyChange ? after.energy : after.agronomy;
      return isJoining
        ? t($ => $.pointsAudit.plain.membershipJoined, { program })
        : t($ => $.pointsAudit.plain.membershipLeft, { program });
    }

    case PointsAuditEventType.PointsImported:
    case PointsAuditEventType.ManualPointsAdjusted: {
      const amount = number(after.amount);
      return amount >= 0
        ? t($ => $.pointsAudit.plain.pointsGiven, { count: Math.abs(amount) })
        : t($ => $.pointsAudit.plain.pointsTaken, { count: Math.abs(amount) });
    }

    case PointsAuditEventType.BehaviorCompleted:
      return t($ => $.pointsAudit.plain.behaviorCompleted, {
        behavior: metadata.behaviorName ?? '',
        date: after.completedDate,
      });

    case PointsAuditEventType.BehaviorReversed:
      return t($ => $.pointsAudit.plain.behaviorReversed, {
        behavior: metadata.behaviorName ?? '',
        date: after.reversedDate,
      });

    case PointsAuditEventType.PointsExpirationChanged:
      return t($ => $.pointsAudit.plain.expirationChanged, {
        count: number(metadata.balancesMoved),
        from: before.pointsExpirationDate,
        to: after.pointsExpirationDate,
      });

    case PointsAuditEventType.TierOverrideSet: {
      const to = after.rankOverride;
      return to === null || to === undefined
        ? t($ => $.pointsAudit.plain.tierCleared)
        : t($ => $.pointsAudit.plain.tierSet, { rank: to });
    }

    case PointsAuditEventType.AccountsLinked:
      return t($ => $.pointsAudit.plain.linked, {
        group: after.accountGroupErpId ?? metadata.customerErpId ?? '',
      });

    case PointsAuditEventType.AccountsUnlinked:
      return t($ => $.pointsAudit.plain.unlinked);

    case PointsAuditEventType.ProductExclusionChanged:
      return Object.keys(after).length
        ? t($ => $.pointsAudit.plain.productExcluded)
        : t($ => $.pointsAudit.plain.productIncluded);

    case PointsAuditEventType.ManualPointsEdited:
      return t($ => $.pointsAudit.plain.manualPointsEdited, {
        from: number(before.amount),
        to: number(after.amount),
      });

    case PointsAuditEventType.PromotionCreated:
      return t($ => $.pointsAudit.plain.promotionCreated, { value: after.value ?? '—' });

    case PointsAuditEventType.PromotionDeleted:
      return t($ => $.pointsAudit.plain.promotionDeleted, { value: before.value ?? '—' });

    case PointsAuditEventType.ProgramSettingsChanged:
      if (!Object.keys(before).length) {
        return t($ => $.pointsAudit.plain.programCreated);
      }
      return Object.keys(after).length
        ? t($ => $.pointsAudit.plain.programSettingsChanged)
        : t($ => $.pointsAudit.plain.programDeleted);

    case PointsAuditEventType.CategoryMappingChanged:
      return t($ => $.pointsAudit.plain.categoryMappingChanged);

    case PointsAuditEventType.PromotionEdited:
      return t($ => $.pointsAudit.plain.promotionEdited, {
        from: before.value ?? '—',
        to: after.value ?? '—',
      });

    case PointsAuditEventType.ImportCompleted:
      return t($ => $.pointsAudit.plain.importCompleted, {
        count: number(metadata.rowsProcessed),
        type: metadata.importType ?? '',
      });

    case PointsAuditEventType.ImportDiscarded:
      // Say plainly that nothing was applied, or this reads as a successful import.
      return t($ => $.pointsAudit.plain.importDiscarded, {
        count: number(metadata.errorCount),
        type: metadata.importType ?? '',
      });

    case PointsAuditEventType.RecalculationQueued:
      // A whole-retailer run lists no farmers; the API counts the import's or sync's instead.
      if (metadata.scope === FarmerReprocessingScope.FullRetailer) {
        return row.farmersAffected === null || row.farmersAffected === undefined
          ? t($ => $.pointsAudit.plain.recalculationQueuedRetailer)
          : t($ => $.pointsAudit.plain.recalculationQueued, { count: row.farmersAffected });
      }
      return t($ => $.pointsAudit.plain.recalculationQueued, {
        count: number(metadata.farmersQueued ?? metadata.rewardsAccountsQueued),
      });

    case PointsAuditEventType.BookingPointsReturned:
      return t($ => $.pointsAudit.plain.bookingReturned, {
        points: number(after.pointsReturned),
      });

    case PointsAuditEventType.Revert:
      return metadata.revertedNothing
        ? t($ => $.pointsAudit.plain.revertedNothing)
        : t($ => $.pointsAudit.plain.reverted, { event: metadata.revertedEventType ?? '' });

    case PointsAuditEventType.SyncCompleted:
      return t($ => $.pointsAudit.plain.syncCompleted);

    default:
      return row.reason ?? row.eventType;
  }
};
